import { create, v } from '@dojo/framework/core/vdom';
import { theme } from '@dojo/framework/core/middleware/theme';
import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';

// make generated .d.ts file valid
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { WNodeFactory, WidgetProperties, DNode } from '@dojo/framework/core/interfaces';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { I18nProperties, DefaultChildrenWNodeFactory } from '@dojo/framework/core/interfaces';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { ThemeProperties } from '@dojo/framework/core/middleware/theme';

import { createNlsMiddleware, lccstate } from 'lcc-state';
import css from '../css/activity.m.css';
import { handleActivityInteraction } from '../support/xapi';

export interface LearningActivityProperties {
	activityname: string;
	minHeight?: string;
	minWidth?: string;
	sizeWarning?: string;
	url?: string;
	title?: string;
	checkSize?: boolean;
}

interface ActivityIcache {
	interacted: boolean;
	listening: boolean;
}

const icache = createICacheMiddleware<ActivityIcache>();

const nls = createNlsMiddleware('activities');
const factory = create({ theme, nls, lccstate, icache }).properties<LearningActivityProperties>();
export const LearningActivity = factory(({ properties, middleware: { theme, nls, lccstate, icache } }) => {
	const { isPlaceholder, messages } = nls();
	if (isPlaceholder) {
		return null;
	}

	const { activityname } = properties();
	const themeCss = theme.classes(css);

	const {
		cia_min_height = '480',
		cia_min_width = '660',
		invalid_size_warning = '[activities] invalid_size_warning'
	} = nls().messages;

	// allow property overriding
	const {
		minHeight = cia_min_height,
		minWidth = cia_min_width,
		sizeWarning = invalid_size_warning,
		url,
		title = '',
		checkSize = true
	} = properties();
	const src = url || messages[`${activityname}_path`];

	const winWidth = window.innerWidth;
	const winHeight = window.innerHeight;

	let isValidSize = true;
	if (checkSize && (winWidth < parseInt(minWidth) || winHeight < parseInt(minHeight))) {
		isValidSize = false;
	}

	const invalidSize = v('span', { classes: [themeCss.warning] }, [sizeWarning]);

	const activityTitle = title || messages[`${activityname}_title`];
	const { get, path } = lccstate;
	const association = get(path('title'));
	const product = get(path('product'));
	document.title = `${title} - ${association} - ${product}`;

	const activityListener = async () => {
		const iframeActive = document.activeElement?.tagName.toLowerCase() === 'iframe';
		if (icache.get('interacted') || !iframeActive) {
			return;
		}

		icache.set('interacted', true);
		await handleActivityInteraction(activityname);
		window.removeEventListener('blur', activityListener);
	};

	if (!icache.get('listening')) {
		window.addEventListener('blur', activityListener);
		icache.set('listening', true);
	}

	return v('div', { key: 'activity', xqa: `activity ${activityname}`, classes: [themeCss.root] }, [
		v('h1', { key: 'activity-title', classes: [themeCss.title] }, [activityTitle]),
		v('div', { key: 'activity-body', xqa: `activity-body ${activityname}`, classes: [themeCss.body] }, [
			!isValidSize
				? invalidSize
				: v('iframe', {
						sandbox: 'allow-same-origin allow-forms allow-scripts',
						seamless: 'seamless',
						title: activityTitle,
						src
				  })
		])
	]);
});
